import React, { useState, useEffect } from 'react';
import { navigate, useQueryParams } from 'hooks/useHookRouterCompat';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import Company, * as Comp from 'data/Company';
import Vendor from 'data/Vendor';
import { isVendor } from 'data/User';
import Page from 'components/UI/Page';
import PageHeading from 'components/UI/PageHeading';
import Tabs from 'components/UI/Tabs';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { Space } from 'styles/themeSpaces'
import { Text, Background } from 'styles/themeColors'
import { FontSize } from 'styles/themeSizes';
import FeatureFlag from 'data/FeatureFlag';
import { loadCompany } from 'requests/company';
import Breadcrumbs from 'components/UI/Breadcrumb';
import useBreadcrumbs from 'hooks/useBreadcrumbs';
import { usePath } from 'hooks/useHookRouterCompat';
import { BorderRadius } from 'lib/styles';
import usePathState from 'hooks/usePathState';
import Button from 'components/UI/Button';
import { ArrowBackIos } from '@mui/icons-material';
import { loadCompanyCustomers } from 'requests/customer';
import Customer from 'data/Customer';
import useCredit from 'hooks/useCredit';
import useCompanyTabs from 'hooks/useCompanyTabs';
import { GENERAL_CONSTANTS } from 'constants/general';
import { BusinessUnit } from 'data/BusinessUnit';

const CompanyAddress = styled.span`
  font-size: ${FontSize.medium};
  line-height: 20px;
  color: ${Text.primary};
`;

const BreadcrumbContainer = styled(FlexboxContainer)`
  cursor: pointer;
  margin-left: ${Space.spHalf};
  margin-right: ${Space.spHalf};
  border-radius: ${BorderRadius.medium};

  &:hover {
    background: rgba(123, 97, 255, 0.05);
  }
`;

const ReturnButton = styled(Button)`
  background-color: ${Background.navy};
  border: 1px solid ${Background.navy};
  color: ${Text.whitePrimary}
  font-size: ${FontSize.medium};
  border-radius: ${BorderRadius.medium}
  text-align: center;
  font-weight: bold;
  margin-top: 10px;
  height: ${Space.sp4xPlus}
`;

const BackIcon = muiStyled(ArrowBackIos)`
  height: 22px;
  color: ${Text.whitePrimary};
  position: relative;
`;

interface Props {
    companyId: number;
    featureFlags: FeatureFlag;
    selectedVendor?: Vendor;
    updateVendor?: (name: string) => void;
    updateBusinessUnit?: (id: number) => void;
    selectedBusinessUnit?: BusinessUnit;
    vendorsList?: Vendor[];
};

const CompanyDetailsV2 = ({
    companyId,
    selectedVendor,
    updateVendor,
    updateBusinessUnit,
    featureFlags,
    selectedBusinessUnit,
    vendorsList
}: Props) => {
    const { user } = useAuth();
    const isVendorAcc = isVendor(user!);
    const [queryParams, setQueryParams] = useQueryParams();
    const [activeTab, setActiveTab] = useState(0);
    const [companyDetails, setCompanyDetails] = useState<Company>({ id: 0, ...Comp.empty });
    const [companyCustomers, setCompanyCustomers] = useState<Customer[]>([]);
    const path = usePath();
    const crumbs = useBreadcrumbs(path);

    const { currentPath } = usePathState();
    const [previousPath, setPreviousPath] = useState(currentPath);

    // Use the credit hook to manage credit-related state and API requests
    const {
        isConnected,
        mdmId,
        creditId,
        creditTrackingMode,
        vendorCompanyDetails,
        creditInfo,
        isInvalid,
        setIsInvalid,
        businessUnitCompanyGetReq,
        mdmApplyReq,
        updateCreditReq,
        createCreditReq,
        loadCompanyInfo,
        error
    } = useCredit({
        businessUnitId: selectedBusinessUnit?.id,
        companyId: companyDetails.companyId
    });

    useEffect(() => {
        setActiveTab(parseInt(queryParams.activeTab));
        setQueryParams({ activeTab: undefined });
    }, []);

    const switchTab = (tabIndex: number) => {
        setActiveTab(tabIndex);
    };

    // Load company info based on business unit
    useEffect(() => {
        if (!companyDetails.companyId || !selectedBusinessUnit) return;
        loadCompanyInfo();
    }, [selectedBusinessUnit, companyDetails.companyId]);

    const getCompanyCustomers = useAPI({
        companyId,
        promiseFn: loadCompanyCustomers,
        onResolve: result => {
            setCompanyCustomers(result);
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    });

    const companyLoadReq = useAPI({
        companyId,
        promiseFn: loadCompany,
        onResolve: (result) => {
            setCompanyDetails(result);
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    });

    // Use our new custom hook for tabs management
    const { isModernUI, tabs, tabContent } = useCompanyTabs({
        featureFlags,
        user,
        isVendorAcc,
        selectedVendor,
        companyDetails,
        companyCustomers,
        selectedBusinessUnit,
        switchTab,
        mdmId,
        creditId,
        isConnected,
        creditInfo,
        vendorCompanyDetails,
        creditTrackingMode,
        mdmApplyReq,
        updateCreditReq,
        createCreditReq,
        isInvalid,
        setIsInvalid,
        updateVendor,
        updateBusinessUnit,
        companyId,
        error,
        vendorsList,
        initialVendor: (companyDetails as any).vendor,
        initialBusinessUnit: (companyDetails as any).businessUnit,
        hasActiveSubscriptions: (companyDetails as any).hasActiveSubscriptions
    });

    if (
        companyLoadReq.isLoading ||
        businessUnitCompanyGetReq.isLoading ||
        getCompanyCustomers.isLoading ||
        mdmApplyReq.isLoading ||
        updateCreditReq.isLoading
    ) {
        return <LoadingIndicator center />;
    }

    const companyName = (
        <BreadcrumbContainer padding={Space.spBase}>
            {companyDetails.displayName}
        </BreadcrumbContainer>
    );

    const breadcrumbs = currentPath.includes('company-details') && previousPath?.includes('order-details')
        ? (
            <FlexboxContainer flexDirection='row'>
                <ReturnButton
                    onClick={() => navigate(previousPath)}
                    leftIcon={(size: string) => <BackIcon style={{ width: size }} />}
                >
                    {GENERAL_CONSTANTS.RETURN_TO_THE_ORDER}
                </ReturnButton>
                <Breadcrumbs containerPadding='10px' crumbs={crumbs} splitOrderName={companyName} />
            </FlexboxContainer>
        )
        : <Breadcrumbs crumbs={crumbs} splitOrderName={companyName} />

    return (
        <Page bgColor={Background.white}>
            {breadcrumbs}
            <FlexboxContainer justifyContent="flex-start" flexDirection="column" bgColor={Background.white} margin={`${Space.sp3x} 0 ${Space.sp4x}`}>
                <PageHeading>{companyDetails.displayName}</PageHeading>
                <FlexboxContainer justifyContent="flex-start">
                    <CompanyAddress>
                        {
                            companyDetails.companyAddress &&
                            companyDetails.countryCode &&
                            `${companyDetails.companyAddress}, ${companyDetails.countryCode}`
                        }
                    </CompanyAddress>
                </FlexboxContainer>
            </FlexboxContainer>

            {isModernUI ? (
                <Tabs
                    height='auto'
                    tabs={tabs}
                    activeTab={activeTab}
                    theme='brandDark'
                    borderRadius='20px'
                    margin='0 24px 8px 0'
                    tabPanelPadding='0 !important'
                    className='brands-tabs'
                >
                    {tabContent}
                </Tabs>
            ) : (
                <Tabs
                    tabs={tabs}
                    activeTab={activeTab}
                >
                    {tabContent}
                </Tabs>
            )}
        </Page>
    );
};

export default CompanyDetailsV2;
